import { createSelector } from '@reduxjs/toolkit';
import { createOptionBindings } from '@ff-os/pdp-shared-redux/slices/sceneSlice';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { PhotoPrintsSceneOptions } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsSceneOptions';
import { isPremiumSupport } from '@ff-scene-plugins/photo-prints-shared/variants';
import { selectAllGroups } from '../slices/groupsSlice';
import { PhotoPrintsPdpState } from '../store';
import { selectAllSizes } from './sizeSelectors';

const optionsBindings = createOptionBindings<PhotoPrintsSceneOptions, PhotoPrintsPdpState>();
const adapterSelectors = optionsBindings.createEntityOptionSelectors(s => s.variants);

export const selectAllVariants = adapterSelectors.selectAll;

export const selectVariantById = adapterSelectors.selectById;

export const selectVariantEntities = adapterSelectors.selectEntities;

export const selectPrintQuality = (state: PhotoPrintsPdpState): PhotoPrintsJson.PrintQuality =>
    state.project.quality ?? 'standard';

export const selectPrintGroups = createSelector([
    selectAllGroups,
    selectVariantEntities,
    selectAllSizes
], (groups, variants, sizes) => {

    const accessorySizeIds = new Set(sizes.filter(size => size.isAccessory === true).map(size => size.id));

    return groups.filter(group => {
        const variant = variants[group.variantId];
        return !!variant && !accessorySizeIds.has(variant.sizeId);
    });
});

export const selectPremium = optionsBindings.createOptionSelector(o => o.premium);

export const selectPremiumCapableGroups = createSelector([
    selectPrintGroups,
    selectVariantEntities
], (printGroups, variantEntities) => {

    return printGroups.filter(group => {
        const variant = variantEntities[group.variantId];
        return !!variant && isPremiumSupport(variant);
    });
});

export const selectIsPremiumAvailable = createSelector([
    selectPremiumCapableGroups,
    selectPremium
], (premiumCapableGroups, premium) => {
    return premiumCapableGroups.length > 0
        && !!premium?.backsideLogoAssetId
        && !!premium?.backsideFontAssetId;
});

export const selectPremiumSkuPairs = createSelector([
    selectPremiumCapableGroups,
    selectVariantEntities
], (premiumCapableGroups, variantEntities) => {

    const upgradedVariants = new Map(premiumCapableGroups
        .map(group => variantEntities[group.variantId])
        .filter((variant): variant is PhotoPrintsJson.Variant => !!variant?.premiumSku)
        .map(variant => [variant.id, variant]));

    return [...upgradedVariants.values()]
        .map(variant => ({ sku: variant.sku, premiumSku: variant.premiumSku! }));
});